import {
  type ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { CreatorPermission } from '@api'
import { useUnit } from 'effector-react'

import {
  $creators,
  $isCreatorProcessCompleted,
  loadCreatorsFx,
  resetCreators,
} from '@/models/creators.ts'
import { useAccount } from '@/modules/accounts/hooks/useAccounts'
import { CreatorFeatures } from '@/modules/accounts/types.ts'
import { hasPermission } from '@/utils/helpers.ts'
import { EVENTS, useCustomEventListener } from '@/utils/useEventListener'

import type { Creator, CreatorContext } from '../types'
import { isActiveCreator } from '../utils'

export const CreatorStateContext = createContext<CreatorContext | undefined>(
  undefined,
)

export const CreatorProvider = ({ children }: { children: ReactNode }) => {
  const { isLoggedIn } = useAccount()
  const creators = useUnit($creators)
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)

  useEffect(() => {
    if (isLoggedIn) {
      loadCreatorsFx()
    }
  }, [isLoggedIn])

  const [selectedCreators, setSelectedCreators] = useState<Creator[]>([])

  const selectableCreators = useMemo(
    () =>
      creators.filter(
        (creator) =>
          isActiveCreator(creator) &&
          (hasPermission(
            creator,
            CreatorFeatures.tracking,
            CreatorPermission.SEE_CHATTER_TRACKING,
          ) ||
            hasPermission(
              creator,
              CreatorFeatures.stats,
              CreatorPermission.SEE_STATS,
            )),
      ),
    [creators],
  )
  const allSelect = useCallback(() => {
    setSelectedCreators([...selectableCreators])
  }, [selectableCreators])

  const allRemove = useCallback(() => {
    setSelectedCreators([])
  }, [])

  const addSelect = useCallback((creator: Creator) => {
    setSelectedCreators((prev) => [
      ...prev.filter((c) => c.id !== creator.id),
      creator,
    ])
  }, [])

  const removeSelect = useCallback((creator: Creator) => {
    setSelectedCreators((prev) => [...prev].filter((x) => x !== creator))
  }, [])

  const [defaultSelectedCreators, setDefaultSelectedCreators] = useState(false)

  useEffect(
    () => {
      if (!isCreatorProcessCompleted) {
        return
      }

      if (!defaultSelectedCreators) {
        allSelect()
      }

      setDefaultSelectedCreators(true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectableCreators, defaultSelectedCreators, isCreatorProcessCompleted],
  )

  useCustomEventListener(EVENTS.LOGOUT, resetCreators)

  const value: CreatorContext = useMemo(
    () => ({
      selection: {
        defaultSelectedCreators,
        selectedCreators,
        selectableCreators,
        allSelect,
        allRemove,
        addSelect,
        removeSelect,
        setSelect: setSelectedCreators,
      },
    }),
    [
      defaultSelectedCreators,
      selectedCreators,
      selectableCreators,
      allSelect,
      allRemove,
      addSelect,
      removeSelect,
      setSelectedCreators,
    ],
  )

  return (
    <CreatorStateContext.Provider value={value}>
      {children}
    </CreatorStateContext.Provider>
  )
}
